import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { ScoreChip } from '~/components/common/score-chip/score-chip';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Popover, PopoverAnchor } from '~/components/ui/popover';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';

import { CompareEnrichedValue, CompareFeedValue, type CompareValueRenderer } from './compare-cell';
import { CompareCellViewer } from './compare-cell-viewer';
import type {
  CompareColumn,
  CompareEditControls,
  CompareFilter,
  CompareLayout,
  CompareRow,
} from './compare-grid.types';
import {
  columnCoverage,
  coverageTally,
  filterCounts,
  filterRows,
  rowFlags,
} from './compare-grid.util';
import { CoverageMeter } from './coverage-meter';

// Column widths (px). The table is fixed-layout and horizontally scrolled, so
// every width is explicit — the pinned columns depend on it to stay aligned.
const W_PRODUCT = 300;
const W_STATUS = 132;

/**
 * Attributes whose values are prose, taxonomy paths or card lists. A single
 * width for every column forces these to wrap into a ragged few characters per
 * line while a `Colour` column sits mostly empty, so widths come in two tiers.
 */
const WIDE_FIELD_KEYS = new Set<string>([
  'description',
  'structuredDescription',
  'structuredTitle',
  'shortTitle',
  'productType',
  'googleProductCategory',
  'productDetail',
  'productHighlight',
  'questionAndAnswer',
  'relatedProduct',
  'variantOption',
  'itemGroupTitle',
  'category',
  'keywords',
]);

/** Per-tier widths for the `Feed | Enriched` pair (split) and the single stacked column (overlay). */
const W = {
  narrow: { feed: 132, enriched: 156, overlay: 190 },
  wide: { feed: 224, enriched: 268, overlay: 320 },
} as const;

const widthsFor = (key: string) => (WIDE_FIELD_KEYS.has(key) ? W.wide : W.narrow);

/**
 * Fixed content height (px) for every value cell. This is what makes rows
 * uniform: a 400-word description and a one-word colour occupy the same box,
 * so the grid scans as a grid. Overflow is hidden and the full value is one
 * double-click away (see `CompareCellViewer`).
 *
 * Split layout gives the whole box to the enriched value (the feed value gets
 * its own column); overlay layout splits it between the stacked halves.
 */
const CELL_CONTENT_H = 64;
const OVERLAY_ENRICHED_H = 42;
const OVERLAY_FEED_H = 20;

const SKELETON_ROWS = 5;
const SKELETON_COLS = 3;

/** Identifies the cell whose expanded viewer is open. */
interface ActiveCell {
  rowIndex: number;
  colIndex: number;
}

/** Row-level review state, derived from diff status — this flow has no per-field model confidence to threshold on. */
type RowStatus = 'failed' | 'needsReview' | 'enriched' | 'unchanged';

const ROW_STATUS_CLASS: Record<RowStatus, string> = {
  failed: 'bg-error-50 text-error border-transparent',
  needsReview: 'bg-warning-50 text-warning border-transparent',
  enriched: 'bg-success-50 text-success border-transparent',
  unchanged: 'text-muted-foreground',
};

/** Classifies a row for the pinned Status column: nothing generated, a value dropped, something added, or no change. */
function rowStatus(row: CompareRow): RowStatus {
  if (row.generationFailed) return 'failed';
  const fields = Object.values(row.fields);
  if (fields.some((f) => f.status === 'removed')) return 'needsReview';
  if (fields.some((f) => f.status === 'enriched' || f.status === 'modified')) return 'enriched';
  return 'unchanged';
}

interface EnrichmentCompareGridProps {
  columns: CompareColumn[];
  rows: CompareRow[];
  /**
   * Renders a single field value. Supplied by the host feature (e.g.
   * `EnrichedFieldCell`) so this common component stays free of
   * enrichment-specific renderers.
   */
  renderValue: CompareValueRenderer;
  /**
   * Renders a value inside the expanded cell viewer, where there's room for the
   * roomier default type scale. Falls back to `renderValue` when omitted.
   */
  renderExpandedValue?: CompareValueRenderer;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /**
   * True when `rows` covers only the current page (the scheduled/Meta flow is
   * server-paginated). Chip counts and coverage are then labelled as
   * page-scoped rather than lot-wide.
   */
  isPageScoped?: boolean;
  /**
   * Supplied by the host feature to enable editing: its presence reveals the
   * "Edited" filter chip, turns the expanded cell viewer into an editor, and
   * shows the save bar. The grid holds no edit state of its own.
   */
  editControls?: CompareEditControls;
  /**
   * Identifies fields whose renderer already tints its own content (card lists),
   * so the cell wrapper skips its background and the value isn't shaded twice.
   */
  isSelfTintedField?: (key: string) => boolean;
  /** Automation hook: root `data-testid`; rows, cells, chips and the viewer derive their ids from it. */
  testId?: string;
}

/**
 * Dense feed-vs-enriched comparison grid — the secondary presentation of an
 * enriched product listing, toggled from the section header.
 *
 * Each attribute occupies a `Feed | Enriched` column pair (split layout) or a
 * single stacked column (overlay layout), with cell tint and marker chips driven
 * by `DiffStatus`. Column headers carry a `before → now +N` coverage tally so
 * the enrichment's contribution per attribute is legible at a glance.
 */
export function EnrichmentCompareGrid({
  columns,
  rows,
  renderValue,
  renderExpandedValue,
  isLoading = false,
  isError = false,
  onRetry,
  isPageScoped = false,
  editControls,
  isSelfTintedField,
  testId = 'compare-grid',
}: EnrichmentCompareGridProps) {
  const { t } = useTranslation('content-enrichment');
  const [layout, setLayout] = useState<CompareLayout>('split');
  const [filter, setFilter] = useState<CompareFilter>('all');
  // The cell whose expanded viewer is open; null when none is.
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  const counts = useMemo(() => filterCounts(rows), [rows]);
  const visibleRows = useMemo(() => filterRows(rows, filter), [rows, filter]);
  // Coverage describes the whole loaded set, not the active filter — filtering to
  // "filled a gap" would otherwise report a misleadingly high coverage figure.
  const coverage = useMemo(() => coverageTally(rows, columns), [rows, columns]);

  const isSplit = layout === 'split';
  const scopeNote = isPageScoped ? t('compareGrid.scope.page') : t('compareGrid.scope.all');

  const gridWidth = columns.reduce((total, column) => {
    const w = widthsFor(column.key);
    return total + (isSplit ? w.feed + w.enriched : w.overlay);
  }, W_PRODUCT + W_STATUS);

  /**
   * Roving-tabindex navigation across enriched cells: the grid is one tab stop
   * and arrow keys move within it, so a wide comparison stays keyboard-scannable
   * without adding hundreds of tab stops.
   */
  const focusCell = useCallback((rowIndex: number, colIndex: number) => {
    const target = bodyRef.current?.querySelector<HTMLElement>(
      `[data-cell-row="${String(rowIndex)}"][data-cell-col="${String(colIndex)}"]`,
    );
    if (!target) return;
    target.focus();
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);

  const handleCellKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const cell = event.currentTarget;
    const r = Number(cell.dataset.cellRow);
    const c = Number(cell.dataset.cellCol);

    // Enter / F2 open the expanded value — the keyboard equivalent of a
    // double-click, matching the spreadsheet convention.
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      setActiveCell({ rowIndex: r, colIndex: c });
      return;
    }

    const moves: Record<string, [number, number]> = {
      ArrowRight: [0, 1],
      ArrowLeft: [0, -1],
      ArrowDown: [1, 0],
      ArrowUp: [-1, 0],
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    focusCell(r + move[0], c + move[1]);
  };

  /** Closing the viewer returns focus to the cell it came from, so keyboard flow isn't lost. */
  const closeViewer = () => {
    const cell = activeCell;
    setActiveCell(null);
    if (cell) focusCell(cell.rowIndex, cell.colIndex);
  };

  // Resolve the active cell's data once, here, rather than threading it through
  // every row. `activeRow`/`activeField` are null whenever the viewer is closed
  // or the row scrolled out of the current filter.
  const activeRow = activeCell ? visibleRows[activeCell.rowIndex] : undefined;
  const activeColumn = activeCell ? columns[activeCell.colIndex] : undefined;
  const activeField = activeRow && activeColumn ? activeRow.fields[activeColumn.key] : undefined;
  const isViewerOpen = !!activeRow && !!activeColumn && !!activeField;

  // Filter chips. "Edited" is withheld until editing exists — a chip that can
  // only ever read 0 is noise.
  const chips: { value: CompareFilter; label: string }[] = [
    { value: 'all', label: t('compareGrid.filters.all') },
    { value: 'needsReview', label: t('compareGrid.filters.needsReview') },
    { value: 'filledGap', label: t('compareGrid.filters.filledGap') },
    ...(editControls
      ? [{ value: 'edited' as CompareFilter, label: t('compareGrid.filters.edited') }]
      : []),
  ];

  const colCount = 2 + columns.length * (isSplit ? 2 : 1);

  return (
    <div data-testid={testId}>
      {/* Controls: layout switch, filter chips, coverage meter */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedTabs<CompareLayout>
            value={layout}
            onChange={setLayout}
            ariaLabel={t('compareGrid.layout.ariaLabel')}
            options={[
              { value: 'split', label: t('compareGrid.layout.split') },
              { value: 'overlay', label: t('compareGrid.layout.overlay') },
            ]}
            className="bg-background border"
            testId={`${testId}-layout-tabs`}
          />

          <div
            className="flex flex-wrap items-center gap-1.5"
            data-testid={`${testId}-filters`}
            role="group"
            aria-label={t('compareGrid.filters.ariaLabel')}
          >
            {chips.map((chip) => (
              <button
                key={chip.value}
                type="button"
                data-testid={`${testId}-filter-${chip.value}`}
                aria-pressed={filter === chip.value}
                onClick={() => {
                  setFilter(chip.value);
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                  filter === chip.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground border-transparent',
                )}
              >
                {chip.label}
                <span
                  className="tabular-nums opacity-70"
                  data-testid={`${testId}-filter-${chip.value}-count`}
                >
                  {counts[chip.value]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <CoverageMeter tally={coverage} scopeNote={scopeNote} testId={`${testId}-coverage`} />
      </div>

      <Popover
        open={isViewerOpen}
        onOpenChange={(next) => {
          if (!next) closeViewer();
        }}
      >
        <div className="mt-4 overflow-auto rounded-lg border bg-white">
          <table
            data-testid={`${testId}-table`}
            className="w-full table-fixed border-collapse text-sm"
            style={{ width: `${String(gridWidth)}px` }}
          >
            <colgroup>
              <col style={{ width: `${String(W_PRODUCT)}px` }} />
              {columns.map((column) => {
                const w = widthsFor(column.key);
                return isSplit ? (
                  [
                    <col key={`${column.key}-f`} style={{ width: `${String(w.feed)}px` }} />,
                    <col key={`${column.key}-e`} style={{ width: `${String(w.enriched)}px` }} />,
                  ]
                ) : (
                  <col key={column.key} style={{ width: `${String(w.overlay)}px` }} />
                );
              })}
              <col style={{ width: `${String(W_STATUS)}px` }} />
            </colgroup>

            <thead className="sticky top-0 z-20 bg-gray-50">
              <tr className="border-b">
                <th
                  scope="col"
                  rowSpan={isSplit ? 2 : 1}
                  className="sticky left-0 z-30 bg-gray-50 px-2 py-1.5 text-left align-bottom text-[11px] font-semibold tracking-wide text-gray-500 uppercase"
                >
                  {t('compareGrid.columns.product')}
                </th>
                {columns.map((column) => {
                  const cov = columnCoverage(rows, column);
                  return (
                    <th
                      key={column.key}
                      data-testid={`${testId}-column-${column.key}`}
                      scope={isSplit ? 'colgroup' : 'col'}
                      colSpan={isSplit ? 2 : 1}
                      className="border-l px-2 py-1.5 text-left align-bottom text-[11px] font-medium text-gray-600"
                    >
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-foreground">{column.label}</span>
                        <span
                          className="text-muted-foreground text-[10px] tabular-nums"
                          data-testid={`${testId}-column-${column.key}-coverage`}
                          title={t('compareGrid.columns.coverageHint', {
                            before: cov.before,
                            total: cov.total,
                            now: cov.now,
                          })}
                        >
                          {cov.before}
                          <span aria-hidden="true"> → </span>
                          {cov.now}
                          {cov.gained > 0 && (
                            <span className="text-success ml-1">+{cov.gained}</span>
                          )}
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th
                  scope="col"
                  rowSpan={isSplit ? 2 : 1}
                  className="sticky right-0 z-30 border-l bg-gray-50 px-2 py-1.5 text-left align-bottom text-[11px] font-semibold tracking-wide text-gray-500 uppercase"
                >
                  {t('compareGrid.columns.status')}
                </th>
              </tr>
              {isSplit && (
                <tr className="border-b">
                  {columns.map((column) => (
                    <Fragment key={column.key}>
                      <th
                        scope="col"
                        className="text-muted-foreground border-l px-2 pb-1.5 text-left text-[10px] font-semibold uppercase"
                      >
                        {t('compareGrid.columns.feed')}
                      </th>
                      <th
                        scope="col"
                        className="text-primary px-2 pb-1.5 text-left text-[10px] font-semibold uppercase"
                      >
                        {t('compareGrid.columns.enriched')}
                      </th>
                    </Fragment>
                  ))}
                </tr>
              )}
            </thead>

            <tbody ref={bodyRef}>
              {isLoading ? (
                Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <tr
                    key={`skel-${String(i)}`}
                    data-testid={`${testId}-loading-row-${String(i)}`}
                    className="border-b"
                  >
                    <td className="sticky left-0 z-10 bg-white px-2 py-1.5 align-top">
                      <div className="flex items-start gap-3">
                        <Skeleton className="h-12 w-12 shrink-0 rounded-md" />
                        <div className="flex-1 space-y-2 pt-1">
                          <Skeleton className="h-3 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                    </td>
                    {Array.from({ length: SKELETON_COLS * (isSplit ? 2 : 1) }, (_, j) => (
                      <td key={`skel-c-${String(j)}`} className="border-l px-2 py-1.5 align-top">
                        <Skeleton className="h-3 w-full" />
                      </td>
                    ))}
                    <td className="sticky right-0 z-10 border-l bg-white px-2 py-1.5 align-top">
                      <Skeleton className="h-5 w-16 rounded-md" />
                    </td>
                  </tr>
                ))
              ) : isError ? (
                <tr data-testid={`${testId}-error`}>
                  <td colSpan={colCount} className="px-2 py-10 text-center">
                    <p className="text-muted-foreground text-sm">{t('compareGrid.error')}</p>
                    {onRetry && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={onRetry}
                        data-testid={`${testId}-retry-button`}
                      >
                        {t('compareGrid.retry')}
                      </Button>
                    )}
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr data-testid={`${testId}-empty`}>
                  <td colSpan={colCount} className="px-2 py-10 text-center">
                    <p className="text-muted-foreground text-sm">
                      {rows.length === 0 ? t('compareGrid.empty') : t('compareGrid.emptyFilter')}
                    </p>
                    {rows.length > 0 && (
                      <Button
                        size="sm"
                        variant="link"
                        className="mt-1"
                        data-testid={`${testId}-clear-filter-button`}
                        onClick={() => {
                          setFilter('all');
                        }}
                      >
                        {t('compareGrid.emptyFilterAction')}
                      </Button>
                    )}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row, rowIndex) => {
                  const status = rowStatus(row);
                  const flags = rowFlags(row);
                  const gapsFilled = Object.values(row.fields).filter(
                    (f) => f.status === 'enriched',
                  ).length;

                  return (
                    <tr
                      key={row.productId}
                      data-testid={`${testId}-row-${row.productId}`}
                      className="border-b last:border-0 hover:bg-gray-50/60"
                    >
                      {/* Pinned product column — thumbnail, enriched title, feed title, id, score */}
                      <td className="sticky left-0 z-10 bg-white px-2 py-1.5 align-top">
                        <div className="flex items-start gap-2" style={{ height: CELL_CONTENT_H }}>
                          <CachedImage
                            src={row.imageUrl}
                            alt=""
                            className="h-9 w-9 shrink-0 rounded"
                          />
                          <div className="flex min-w-0 flex-col">
                            {/* Two lines then ellipsis — long titles are the main row-height offender */}
                            <span
                              className="text-foreground line-clamp-2 text-xs leading-4 font-semibold"
                              data-testid={`${testId}-row-${row.productId}-title`}
                            >
                              {row.title}
                            </span>
                            <span className="text-muted-foreground/70 line-clamp-1 text-[11px] leading-4">
                              {row.feedTitle ?? t('compareGrid.cell.noFeedValue')}
                            </span>
                            <span className="mt-auto flex items-center gap-1.5">
                              <span className="text-muted-foreground truncate font-mono text-[10px]">
                                {row.productId}
                              </span>
                              {row.score != null && (
                                <span className="origin-left scale-90">
                                  <ScoreChip
                                    score={row.score}
                                    testId={`${testId}-row-${row.productId}-score`}
                                  />
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </td>

                      {columns.map((column, colIndex) => {
                        const field = row.fields[column.key];
                        if (!field) {
                          return isSplit ? (
                            <Fragment key={column.key}>
                              <td className="border-l px-2 py-1.5 align-top" />
                              <td className="px-2 py-1.5 align-top" />
                            </Fragment>
                          ) : (
                            <td key={column.key} className="border-l px-2 py-1.5 align-top" />
                          );
                        }

                        const isActive =
                          activeCell?.rowIndex === rowIndex && activeCell.colIndex === colIndex;
                        const openViewer = () => {
                          setActiveCell({ rowIndex, colIndex });
                        };

                        const enrichedCell = (
                          <td
                            data-testid={`${testId}-cell-${row.productId}-${column.key}`}
                            data-cell-row={rowIndex}
                            data-cell-col={colIndex}
                            tabIndex={rowIndex === 0 && colIndex === 0 ? 0 : -1}
                            onKeyDown={handleCellKeyDown}
                            onDoubleClick={openViewer}
                            aria-label={t('compareGrid.cell.ariaLabel', { field: column.label })}
                            title={t('compareGrid.cell.expandHint')}
                            className={cn(
                              'focus-visible:ring-primary relative cursor-zoom-in px-2 py-1.5 align-top focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
                              !isSplit && 'border-l',
                              isActive && 'ring-primary ring-2 ring-inset',
                            )}
                          >
                            {/* Anchors the expanded viewer to this cell. Only the active
                                cell renders it, so Radix tracks a single position. */}
                            {isActive && (
                              <PopoverAnchor asChild>
                                <span
                                  aria-hidden="true"
                                  className="pointer-events-none absolute inset-0"
                                />
                              </PopoverAnchor>
                            )}
                            <CompareEnrichedValue
                              field={field}
                              renderValue={renderValue}
                              showFeedBeneath={!isSplit}
                              selfTinted={isSelfTintedField?.(field.key) ?? false}
                              clampHeight={isSplit ? CELL_CONTENT_H : OVERLAY_ENRICHED_H}
                              feedClampHeight={OVERLAY_FEED_H}
                            />
                          </td>
                        );

                        if (!isSplit) return <Fragment key={column.key}>{enrichedCell}</Fragment>;

                        return (
                          <Fragment key={column.key}>
                            {/* Double-clicking either half opens the same comparison */}
                            <td
                              data-testid={`${testId}-feed-cell-${row.productId}-${column.key}`}
                              onDoubleClick={openViewer}
                              title={t('compareGrid.cell.expandHint')}
                              className="cursor-zoom-in border-l px-2 py-1.5 align-top"
                            >
                              <CompareFeedValue
                                value={field.feedValue}
                                renderValue={renderValue}
                                fieldKey={field.key}
                                clampHeight={CELL_CONTENT_H}
                              />
                            </td>
                            {enrichedCell}
                          </Fragment>
                        );
                      })}

                      {/* Pinned status column */}
                      <td className="sticky right-0 z-10 border-l bg-white px-2 py-1.5 align-top">
                        <div className="flex flex-col items-start gap-1.5">
                          <Badge
                            variant="outline"
                            data-testid={`${testId}-row-${row.productId}-status-badge`}
                            data-status={status}
                            className={cn('w-fit', ROW_STATUS_CLASS[status])}
                          >
                            {t(`compareGrid.status.${status}`)}
                          </Badge>
                          {flags.filledGap && (
                            <span className="text-muted-foreground text-[10px]">
                              {t('compareGrid.status.gapsFilled', { count: gapsFilled })}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Expanded cell — the full, unclamped value for whichever cell was opened */}
        {activeRow && activeColumn && activeField && (
          <CompareCellViewer
            // Keyed by cell so moving between cells remounts with a fresh draft.
            key={`${activeRow.productId}::${activeField.key}`}
            column={activeColumn}
            field={activeField}
            productTitle={activeRow.title}
            renderValue={renderExpandedValue ?? renderValue}
            selfTinted={isSelfTintedField?.(activeField.key) ?? false}
            onClose={closeViewer}
            testId={`${testId}-viewer`}
            {...(editControls && {
              // Per-cell save: persists this one cell immediately, passing the column
              // so the host has the wire field name the endpoint is keyed by. `value`
              // is already in the attribute's shape (parsed for `json` fields).
              onCommit: (value: unknown) =>
                editControls.commitCell(activeRow.productId, activeColumn, value),
              onReset: () => {
                editControls.resetCell(activeRow.productId, activeField.key);
              },
            })}
          />
        )}
      </Popover>

      <p className="text-muted-foreground mt-3 text-xs" data-testid={`${testId}-footnote`}>
        {t('compareGrid.footnote', { shown: visibleRows.length, loaded: rows.length })} {scopeNote}
      </p>
    </div>
  );
}
